import { createSlice } from '@reduxjs/toolkit';

import { objectSelector as _get } from '../../utils';

const initialState = {
  preOrder: {},
  addresses: [],
  addressId: null,
  agreement: false,
  basket: {},
  canGuestPurchase: false,
  cardHolder: '',
  cardCVV: '',
  cardMonth: '',
  cardNumber: '',
  cardYear: '',
  cardType: {},
  country: {},
  currency: 'TL',
  currentTab: 'address',
  billingId: null,
  deliveryOptionId: null,
  deliveryOptions: [],
  errors: null,
  fundsTransferAccounts: [],
  fundsTransferAccountId: null,
  creditPaymentOptions: [],
  creditPaymentOptionId: null,
  payOnDeliveryMethods: [],
  payOnDeliveryMethod: null,
  giftable: false,
  giftNote: null,
  hasCampaigns: false,
  installmentId: null,
  installmentMessages: [],
  installments: [],
  isReady: false,
  pending: false,
  paymentOptions: [],
  unavailablePaymentOptions: [],
  paymentOptionId: null,
  phoneNumber: null,
  sameBillAddress: true,
  shippingOptions: [],
  shippingId: null,
  shippingAmount: '',
  totalAmount: '',
  totalAmounWithInterest: '',
  useThreeD: false,
  sampleProducts: [],
  selectedSampleProducts: [],
  sampleProductMaxAllowed: 0,
  sampleProductsSaved: false,
  loyaltyCardNumber: null,
  loyaltyTotalAmount: null,
  loyaltyAmount: null,
  loyaltyAppliedAmount: null,
  retailStores: [],
  selectedRetailStore: null,
  masterPassOrder: null,
  hasRewards: false,
  binNumberLength: 6,
  contextExtras: {},
  hepsipayAvailability: false
};

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setReady(state) {
      state.isReady = true;
    },
    setTab(state, { payload }) {
      state.currentTab = payload;
    },
    setCardHolder(state, { payload }) {
      state.cardHolder = payload;
    },
    setCardCVV(state, { payload }) {
      state.cardCVV = payload;
    },
    setCardMonth(state, { payload }) {
      state.cardMonth = payload;
    },
    setCardNumber(state, { payload }) {
      state.cardNumber = payload;
    },
    setCardYear(state, { payload }) {
      state.cardYear = payload;
    },
    setAgreement(state, { payload }) {
      state.agreement = payload;
    },
    setThreeD(state, { payload }) {
      state.useThreeD = payload;
    },
    setSameBillAddress(state, { payload }) {
      state.sameBillAddress = payload;
    },
    setShippingAddressId(state, { payload }) {
      state.addressId = payload;
    },
    setBillingAddressId(state, { payload }) {
      state.billingId = payload;
    },
    setAddresses(state, { payload }) {
      state.addresses = payload;
    },
    setFundsTransferAccountId(state, { payload }) {
      state.fundsTransferAccountId = payload;
    },
    setCreditPaymentOptionId(state, { payload }) {
      state.creditPaymentOptionId = payload;
    },
    setPayOnDeliveryMethodName(state, { payload }) {
      state.payOnDeliveryMethod = payload;
    },
    setSelectedSampleProducts(state, { payload }) {
      state.selectedSampleProducts = payload;
    },
    setSampleProductsSaved(state, { payload }) {
      state.sampleProductsSaved = payload;
    },
    setIndexPageContext(state, { payload }) {
      state.canGuestPurchase = _get(payload, 'can_guest_purchase', initialState.canGuestPurchase);
      state.giftable = _get(payload, 'has_gift_box', initialState.giftable);
    },
    setDeliveryOptionSelectionPageContext(state, { payload }) {
      state.deliveryOptions = _get(payload, 'delivery_options', initialState.deliveryOptions);
    },
    setAddressSelectionPageContext(state, { payload }) {
      state.addresses = _get(payload, 'addresses', initialState.addresses);
      state.country = _get(payload, 'country', initialState.country);
    },
    setPickupLocationSelectionPageContext(state, {payload}) {
      state.addresses = _get(payload, 'addresses', initialState.addresses);
      state.country = _get(payload, 'country', initialState.country);
    },
    setShippingOptionSelectionPageContext(state, { payload }) {
      const deliveryOptions = state.deliveryOptions;
      const retailStoreDeliveryOption = deliveryOptions.find(option => option.delivery_option_type === 'retail_store');
      const currentDeliveryOptionId = state.deliveryOptionId;
      const shippingOptions = _get(payload, 'shipping_options', initialState.shippingOptions);

      state.shippingOptions = currentDeliveryOptionId === retailStoreDeliveryOption?.pk ?
        shippingOptions.filter(opt => opt.slug === 'RetailStore')
        : shippingOptions;
    },
    setPaymentOptionSelectionPageContext(state, { payload }) {
      state.paymentOptions = _get(payload, 'payment_options', initialState.paymentOptions);
      state.unavailablePaymentOptions = _get(payload, 'unavailable_options', initialState.unavailablePaymentOptions);
    },
    setCreditCardConfirmationPageContext(state, { payload }) {
      state.hasCampaigns = _get(payload, 'has_campaigns', initialState.hasCampaigns);
    },
    setInstallmentSelectionPageContext(state, { payload }) {
      state.installments = _get(payload, 'installments', initialState.installments);
      state.installmentMessages = _get(payload, 'installment_messages', initialState.installmentMessages);
      state.cardType = _get(payload, 'card_type', initialState.cardType);
    },
    setMasterpassInstallmentPageContext(state, { payload }) {
      state.installments = _get(payload, 'installments', initialState.installments);
      state.installmentMessages = _get(payload, 'installment_messages', initialState.installmentMessages);
      state.cardType = _get(payload, 'card_type', initialState.cardType);
    },
    setMasterpassCompletePageContext(state, { payload }) {
      state.masterPassOrder = payload;
    },
    setFundsTransferChoicePageContext(state, { payload }) {
      state.fundsTransferAccounts = _get(payload, 'bank_accounts', initialState.fundsTransferAccounts);
    },
    setCreditPaymentSelectionPageContext(state, { payload }) {
      state.creditPaymentOptions = _get(payload, 'credit_payment_options', initialState.creditPaymentOptions);
    },
    setWalletSelectionPageContext(state, { payload }) {
      let isHepsipaySuccess = null;
      
      if (payload?.paymentMethod === "hepsipay" && payload?.paymentData?.data?.Success === true) {
        isHepsipaySuccess = payload?.paymentData?.data?.Success;
      }

      state.hepsipayAvailability = isHepsipaySuccess;
    },
    setPayOnDeliveryPaymentChoicePageContext(state, { payload }) {
      state.payOnDeliveryMethods = _get(payload, 'payment_choices', initialState.payOnDeliveryMethods);
    },
    setSampleProductPageContext(state, { payload }) {
      state.sampleProductMaxAllowed = _get(payload, 'allowed_quantity', initialState.sampleProductMaxAllowed);
      state.sampleProducts = _get(payload, 'sample_products', initialState.sampleProducts);
    },
    setLoyaltyCardPageContext(state, { payload }) {
      state.loyaltyCardNumber = _get(payload, 'card_number', initialState.loyaltyCardNumber);
      state.loyaltyTotalAmount = _get(payload, 'loyalty_total_amount', initialState.loyaltyTotalAmount);
      state.loyaltyAmount = _get(payload, 'loyalty_amount', initialState.loyaltyAmount);
      state.loyaltyAppliedAmount = _get(payload, 'applied_amount', initialState.loyaltyAppliedAmount);
    },
    setRetailStoreSelectionPageContext(state, { payload }) {
      state.retailStores = _get(payload, 'retail_stores', initialState.retailStores);
      state.addresses = _get(payload, 'addresses', initialState.addresses);
      state.country = _get(payload, 'country', initialState.country);
    },
    setGiftNote(state, { payload }) {
      state.giftNote = payload;
    },
    clearErrors(state) {
      state.errors = null;
    },
    setPending(state, { payload }) {
      state.pending = payload;
    },
    setErrors(state, { payload }) {
      state.errors = payload;
    },
    setPreOrder(state, { payload }) {
      
      state.preOrder = payload;
      state.basket = _get(payload, 'basket', initialState.basket);
      state.addressId = _get(payload, 'shipping_address.pk', initialState.addressId);
      state.billingId = _get(payload, 'billing_address.pk', initialState.billingAddress);
      state.currency = _get(payload, 'currency_type_label', initialState.currency);
      state.deliveryOptionId = _get(payload, 'delivery_option.pk', initialState.deliveryOptionId);
      state.installmentId = _get(payload, 'installment.pk', initialState.installmentId);
      state.paymentOptionId = _get(payload, 'payment_option.pk', initialState.paymentOptionId);
      state.shippingId = _get(payload, 'shipping_option.pk', initialState.shippingOptionId);
      state.phoneNumber = _get(payload, 'phone_number', initialState.phoneNumber) || _get(payload, 'shipping_address.phone_number', initialState.phoneNumber);
      state.giftNote = _get(payload, 'gift_box.note', initialState.giftNote);
      state.shippingAmount = _get(payload, 'shipping_amount', initialState.shippingAmount);
      state.totalAmount = _get(payload, 'total_amount', initialState.totalAmount);
      state.totalAmountWithInterest = _get(payload, 'total_amount_with_interest', initialState.totalAmount);
      state.selectedSampleProducts = _get(payload, 'sample_products', initialState.totalAmount) || [];
      state.sampleProductsSaved = payload.hasOwnProperty('sample_products');
      state.selectedRetailStore = _get(payload, 'retail_store', initialState.selectedRetailStore);
      state.contextExtras = _get(payload, 'context_extras', state.preOrder?.wallet_method === 'hepsipay' ? initialState.contextExtras : {});
    },
    setRewards(state, { payload }) {
      state.hasRewards = payload;
    },
    setBinNumberLength(state, { payload }) {
      state.binNumberLength = payload;
    }
  }
});

export const {
  setReady,
  setTab,
  setCardHolder,
  setCardCVV,
  setCardMonth,
  setCardNumber,
  setCardYear,
  setAgreement,
  setThreeD,
  setSameBillAddress,
  clearErrors,
  setErrors,
  setPending,
  setPreOrder,
  setFundsTransferAccountId,
  setCreditPaymentOptionId,
  setPayOnDeliveryMethodName,
  setShippingAddressId,
  setBillingAddressId,
  setAddresses,
  setSelectedSampleProducts,
  setSampleProductsSaved,
  setGiftNote,
  setRewards,
  setBinNumberLength
} = checkoutSlice.actions;

export const actions = checkoutSlice.actions;

export default checkoutSlice.reducer;
